import { useMemo, useRef, useState } from 'react';

import { Card, CardContent, CardHeader, CardTitle, LineChart, StackedBarChart } from '@mesh.ai/rabbit';
import OverviewSection from 'Deer/components/Overview/OverviewSection';
import { getIntervalCount } from 'Deer/helpers/intervalMonthCalculator';
import { GOAL_STATUS_NODES } from 'Deer/hooks/dataHooks/constants';
import { useGetOverviewMetrics } from 'Deer/hooks/dataHooks/metrics/useGetOverviewMetrics';
import { useGetOverviewMetricsExplanations } from 'Deer/hooks/dataHooks/metrics/useGetOverviewMetricsExplanations';
import useLocalStorageCards from 'Deer/hooks/useLocalStorageCards';
import { MetricCards } from 'Deer/metricCards/MetricCards';
import { MODULE_KEYS_METRIC_CARDS_DEER } from 'Deer/metricCards/constants';
import { getGoalOverviewMetricCardPersistenceKey } from 'Deer/pages/Team/DirectReports/DirectReportsOverview/cards/Goals/DirectReportsGoalsOverview';
import { DeerBarChart } from 'Deer/pages/Team/DirectReports/DirectReportsOverview/cards/common/DeerBarChart';
import { DeerLineChart } from 'Deer/pages/Team/DirectReports/DirectReportsOverview/cards/common/DeerLineChart';
import { transformedCardsForGoals } from 'Deer/pages/Team/DirectReports/DirectReportsOverview/utlis';
import { setIsCardOpen } from 'Deer/slice/DeerCollapsibleCard.slice';
import { useSelectVisierAuthToken } from 'Deer/slice/UserAccessesSlice';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { format } from 'date-fns';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useGoalPreferencesContext } from 'screens/admin/Objectives/GoalPreferences/context/GoalPreferencesProvider';
import { useTimeBoundTimePeriods } from 'screens/home/summary/components/Cards/Goals/hooks';
import { checkSandboxStaticDataFeaturesEnabled, checkNewStaticDataFeaturesEnabled } from 'utils/helpers/featureFlags';
import { getUniqueCardKey } from 'utils/helpers/getUniqueCardKey';

// import { useTimeBoundTimePeriods } from 'screens/home/summary/components/Cards/Goals/hooks';
import { modifyString, StringModifier } from 'constants/copyHelpers';

import { barChartData, staticCardsData, lineChartData } from './goalsMockData';

// using dummy data as per the requirement
export const SkipTeamGoalsOverview = () => {
  const history = useHistory();
  const visierAuthToken = useSelectVisierAuthToken();
  const dispatch = useDispatch();
  const barChartRef = useRef();
  const lineChartRef = useRef();
  const { moduleName } = useOrganisationSettings();

  const { data: timelineData } = useTimeBoundTimePeriods();

  const { uuid: currentTimelineUuId } = timelineData?.current ?? {};

  const currentURL = history.location.pathname;

  const localStorageKey = getUniqueCardKey({ url: currentURL, cardId: 'mySkipTeamGoalsOverview' });

  const isCardOpen = useLocalStorageCards({
    localStorageKey,
    card: 'goalsOverview',
    section: 'mySkipTeam',
  });

  const currentDate = new Date();

  const [correlationId] = useState(window.crypto.randomUUID());

  // @ts-expect-error - goalPreferenceDetails is not defined in the type
  const { goalPreferenceDetails = {} } = useGoalPreferencesContext();

  // Format the date in the desired format
  const formattedDate = format(currentDate, 'yyyy-MM-dd');

  const { data, isLoading, isError, isFetched } = useGetOverviewMetrics<
    ['GOAL_COUNT_BY_STATUS', 'ACTIVE_GOAL_PROGRESS_AVERAGE']
  >({
    metrics: [
      {
        analyticsType: 'GOAL',
        metricType: 'GOAL_COUNT_BY_STATUS',
        params: {
          mock: false,
          time: {
            endDate: formattedDate,
            intervalCount: getIntervalCount(goalPreferenceDetails?.startMonth),
            periodCount: 1,
            periodType: 'MONTH',
          },
          token: visierAuthToken,
        },
      },
      {
        analyticsType: 'GOAL',
        metricType: 'ACTIVE_GOAL_PROGRESS_AVERAGE',
        params: {
          mock: false,
          time: {
            endDate: formattedDate,
            intervalCount: getIntervalCount(goalPreferenceDetails?.startMonth),
            periodCount: 1,
            periodType: 'MONTH',
          },
          token: visierAuthToken,
        },
      },
    ],
    cohortId: -3,
    group: 'GOAL',
    correlationId,
  });

  const { data: kpiCardData } = useGetOverviewMetrics<
    ['ACTIVE_GOAL_PROGRESS_AVERAGE_KPI', 'EMP_COUNT_NO_GOALS_SET', 'EMP_COUNT_NO_GOAL_CHECKINS']
  >({
    metrics: [
      {
        analyticsType: 'GOAL',
        metricType: 'ACTIVE_GOAL_PROGRESS_AVERAGE_KPI',
        params: {
          mock: false,
          time: {
            endDate: formattedDate,
            intervalCount: 1,
            periodCount: 1,
            periodType: 'MONTH',
          },
          token: visierAuthToken,
        },
      },
      {
        analyticsType: 'GOAL',
        metricType: 'EMP_COUNT_NO_GOAL_CHECKINS',
        params: {
          timeBoundDetailsId: currentTimelineUuId,
        },
      },
      {
        analyticsType: 'GOAL',
        metricType: 'EMP_COUNT_NO_GOALS_SET',
        params: {
          timeBoundDetailsId: currentTimelineUuId,
        },
      },
    ],
    cohortId: -3,
  });

  const cardsDataFromApi = useMemo(
    () =>
      kpiCardData &&
      transformedCardsForGoals({
        ACTIVE_GOAL_PROGRESS_AVERAGE_KPI: kpiCardData.ACTIVE_GOAL_PROGRESS_AVERAGE_KPI,
        EMP_COUNT_NO_GOAL_CHECKINS: kpiCardData.EMP_COUNT_NO_GOAL_CHECKINS,
        EMP_COUNT_NO_GOALS_SET: kpiCardData.EMP_COUNT_NO_GOALS_SET,
      }),
    [kpiCardData]
  );

  const { data: explanationData } = useGetOverviewMetricsExplanations({
    cohortId: -3,
    group: 'GOAL',
    correlationId,
    enabled: isFetched,
  });

  const explanation: {
    explanation?: string;
    sentiment?: 'warning' | 'success';
  } = useMemo(
    () =>
      explanationData && explanationData[0]
        ? {
            explanation: explanationData[0].explanation,
            sentiment: explanationData[0].sentiment === 'negative' ? 'warning' : 'success',
          }
        : {},
    [explanationData]
  );

  const handleChange = (newVal: boolean) => {
    dispatch(
      setIsCardOpen({
        card: 'goalsOverview',
        section: 'mySkipTeam',
        isCardOpen: newVal,
        localStorageKey,
      })
    );
    return newVal;
  };

  return (
    <OverviewSection
      isError={
        checkSandboxStaticDataFeaturesEnabled() || checkNewStaticDataFeaturesEnabled() ? false : !visierAuthToken
      }
      emptyStateDescription="Create, own or collaborate on goals to unlock valuable insights on your team's performance"
      open={isCardOpen}
      onOpenChange={handleChange}
      title={`${modifyString(moduleName, StringModifier.TITLE_CASE)}s`}
      topSection={
        <MetricCards
          columns={`repeat(${
            checkSandboxStaticDataFeaturesEnabled() || checkNewStaticDataFeaturesEnabled()
              ? staticCardsData?.length
              : cardsDataFromApi?.length
          }, 1fr)`}
          cards={
            checkSandboxStaticDataFeaturesEnabled() || checkNewStaticDataFeaturesEnabled()
              ? staticCardsData
              : cardsDataFromApi
          }
          cardTitle="Metrics"
          module={MODULE_KEYS_METRIC_CARDS_DEER.GOAL}
          gridStatePersistenceKey={getGoalOverviewMetricCardPersistenceKey({
            tabId: 'Insights',
            viewId: '-3',
            uniqueId: 'GroupByIndividualGrid',
          })}
          isOverviewPageCard
        />
      }
      showCaption={
        checkSandboxStaticDataFeaturesEnabled() || checkNewStaticDataFeaturesEnabled() || explanation.explanation
      }
      subTitleColor={explanation.sentiment ?? 'success'}
      subTitle={
        explanation.explanation ??
        'Your teams are making excellent progress with their goals this cycle. Consider praising them publicly to encourage them. Additionally, in your next check-in with George explore why many goals were dropped mid-cycle.'
      }
    >
      {/* Goals Distribution Chart */}
      <Card
        style={{
          marginBottom: '8px',
        }}
      >
        <CardHeader>
          <CardTitle>Distribution</CardTitle>
        </CardHeader>
        <CardContent>
          {checkSandboxStaticDataFeaturesEnabled() || checkNewStaticDataFeaturesEnabled() ? (
            <div style={{ height: '240px' }} className="rabbit-ds">
              <StackedBarChart
                ref={barChartRef}
                mode="full"
                nodes={barChartData?.nodes}
                series={barChartData?.series}
                tooltipTitle="Distribution"
                xAxisLabel="Team members"
                xAxisValues={barChartData?.xaxis}
                yAxisLabel="Goal count"
                seriesSelectorTitle="Status"
                maxNodesInMiniMode={0}
              />
            </div>
          ) : (
            <DeerBarChart
              ref={barChartRef}
              tooltipTitle="Distribution"
              xAxisLabel="Team members"
              yAxisLabel="Goal count"
              seriesSelectorTitle="Status"
              series={data?.GOAL_COUNT_BY_STATUS?.series}
              xAxisValues={data?.GOAL_COUNT_BY_STATUS?.xAxis}
              isLoading={isLoading}
              isError={isError}
              nodes={GOAL_STATUS_NODES}
              hasNoData={data?.GOAL_COUNT_BY_STATUS?.xAxis?.length === 0}
              emptyStateDescription="Create, own or collaborate on goals to unlock valuable insights on your team's performance"
            />
          )}
        </CardContent>
      </Card>
      {/* Goals Progress Chart */}

      <Card>
        <CardHeader>
          <CardTitle>Progress</CardTitle>
        </CardHeader>
        <CardContent>
          {checkSandboxStaticDataFeaturesEnabled() || checkNewStaticDataFeaturesEnabled() ? (
            <div style={{ height: '240px' }} className="rabbit-ds">
              <LineChart
                ref={lineChartRef}
                nodes={lineChartData?.nodes}
                xAxisValues={lineChartData?.xaxis}
                series={lineChartData?.series}
                xAxisLabel="Months"
                yAxisLabel="Goal progress"
                mode="full"
                connectNulls
                yAxisUnitSymbol={{
                  symbol: '%',
                  position: 'post',
                }}
                tooltipTitle="Progress"
                seriesSelectorTitle="Team members"
                maxNodesInMiniMode={0}
              />
            </div>
          ) : (
            <DeerLineChart
              ref={lineChartRef}
              xAxisLabel="Months"
              yAxisLabel="Goal progress%"
              tooltipTitle="Progress"
              seriesSelectorTitle="Team members"
              series={data?.ACTIVE_GOAL_PROGRESS_AVERAGE.series}
              xAxisValues={data?.ACTIVE_GOAL_PROGRESS_AVERAGE.xAxis}
              isLoading={isLoading}
              isError={isError}
              nodes={data?.ACTIVE_GOAL_PROGRESS_AVERAGE.nodes}
              hasNoData={data?.ACTIVE_GOAL_PROGRESS_AVERAGE?.series.length === 0}
              emptyStateDescription="Create, own or collaborate on goals to unlock valuable insights on your team's performance"
              yAxisUnitSymbol={{
                symbol: '%',
                position: 'post',
              }}
            />
          )}
        </CardContent>
      </Card>
    </OverviewSection>
  );
};
